import { convertLetters } from "@/utils/letterConversion";
import {
    ChevronDown,
    Feather,
    Gem,
    Heart,
    Link2,
    Shield,
    Sprout,
} from "lucide-react";

export default function StreamTeamAboutPage() {
    return (
        <div className="w-full text-zinc-100">
            <section className="relative flex min-h-[52vh] items-center justify-center px-4 sm:min-h-[58vh] sm:px-6">
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,193,7,0.12),rgba(0,0,0,0.88)_56%)]" />
                <div className="relative z-10 flex w-full max-w-5xl flex-col items-center text-center">
                    <img
                        src="/ravenguard-logo-ps.svg"
                        alt="Ravenguard"
                        className="w-full max-w-105 drop-shadow-[0_0_24px_rgba(255,193,7,0.25)]"
                    />
                    <p className="font-garamond mt-6 max-w-3xl text-lg text-zinc-300 sm:text-xl">
                        The official home of the VANDERHEIM RAVENGUARD (VRX)
                    </p>
                </div>
                <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-zinc-500">
                    <ChevronDown className="h-5 w-5 animate-bounce" />
                </div>
            </section>

            <section className="border-y border-zinc-800 bg-zinc-900/80 px-4 py-12 sm:px-6">
                <div className="mx-auto grid w-full max-w-6xl items-center gap-8 lg:grid-cols-[1.15fr_0.85fr]">
                    <div>
                        <SectionTitle
                            icon="🐦‍⬛"
                            title={
                                <>
                                    {convertLetters("ABOUT THE")}{" "}
                                    <a
                                        href="https://ravenguard.vanderheim.com/"
                                        className="text-brand underline-offset-4 hover:underline"
                                    >
                                        {convertLetters("RAVENGUARD")}
                                    </a>
                                </>
                            }
                        />
                        <p className="font-garamond mt-3 text-base leading-relaxed text-zinc-300 sm:text-lg">
                            The VANDERHEIM RAVENGUARD (VRX) is the official
                            stream team of VANDERWOODTV and the wider VANDERHEIM
                            community.
                        </p>
                        <p className="font-garamond mt-3 text-base leading-relaxed text-zinc-300 sm:text-lg">
                            A carefully curated fellowship of trusted creators
                            who have earned their wings and are entrusted to
                            carry the standard of VANDERHEIM beyond its borders.
                            More than a traditional stream team, the{" "}
                            <a
                                href="https://ravenguard.vanderheim.com/"
                                className="text-brand underline-offset-4 hover:underline"
                            >
                                RAVENGUARD
                            </a>{" "}
                            is fully integrated with the VANDERHEIM Village
                            Portal, allowing creators and supporters to share
                            progression through a single connected ecosystem.
                        </p>
                    </div>

                    <div className="flex justify-center lg:justify-end">
                        <img
                            src="/vrx-rune-logo.svg"
                            alt="VRX Rune"
                            className="h-52 w-auto sm:h-64"
                        />
                    </div>
                </div>
            </section>

            <section className="mx-auto w-full max-w-6xl px-4 py-12 sm:px-6 sm:py-14">
                <InfoBlock
                    icon={<Heart className="h-5 w-5 text-violet-400" />}
                    title="Supporting the Ravenguard"
                >
                    <p>Supporting THE RAVENGUARD is easy.</p>
                    <p className="mt-3">
                        Every member offers a unique Channel Point Runestone.
                        Collecting a Runestone earns Reputation Points (RP) for
                        both you and the creator, helping them progress through
                        the VRX while increasing your own standing within both
                        the{" "}
                        <a
                            href="https://ravenguard.vanderheim.com/"
                            className="text-brand underline-offset-4 hover:underline"
                        >
                            RAVENGUARD
                        </a>{" "}
                        and VANDERHEIM communities. You can find a list of all
                        the active members as well as those currently live at{" "}
                        <a
                            href="https://ravenguard.vanderheim.com/"
                            className="text-brand underline-offset-4 hover:underline"
                        >
                            THE ROOKERY!
                        </a>
                    </p>
                    <p className="mt-3">
                        Each member's unique Runestone can be acquired once
                        every 7 days, encouraging supporters to return
                        regularly, build their Runestone collection, and
                        continue earning RP by supporting fellow{" "}
                        <a
                            href="https://ravenguard.vanderheim.com/"
                            className="text-brand underline-offset-4 hover:underline"
                        >
                            RAVENGUARD
                        </a>{" "}
                        members.
                    </p>
                    <p className="mt-3">
                        As the RP system grows, new ways to earn Reputation will
                        continue to be introduced, rewarding ongoing
                        participation and engagement throughout the wider
                        VANDERHEIM ecosystem.
                    </p>
                </InfoBlock>

                <InfoBlock
                    icon={<Shield className="h-5 w-5 text-rose-300" />}
                    title="Forge Your Shield"
                >
                    <p>
                        Before collecting your first Runestone, you'll need to{" "}
                        <a
                            href="https://village.vanderheim.com/"
                            className="text-brand underline-offset-4 hover:underline"
                        >
                            forge
                        </a>{" "}
                        your Shield.
                    </p>
                    <p className="mt-3">
                        Your Shield is your permanent identity within
                        VANDERHEIM, allowing your Runestone collection,
                        Reputation, Shield Tags, progression, leaderboards, and
                        future rewards to be tracked across the entire
                        ecosystem. This also encourages supporters to explore
                        other RAVENGUARD communities ensuring the broader
                        community grows as each member thrives individually.
                    </p>
                    <p className="mt-3">
                        Once forged, your Shield becomes your place within the
                        village and the foundation of your journey through both
                        VANDERHEIM and the RAVENGUARD reward systems.
                    </p>
                </InfoBlock>

                <InfoBlock
                    icon={<Gem className="h-5 w-5 text-sky-300" />}
                    title="Build Your Collection"
                >
                    <p>Every RAVENGUARD member carries a unique Runestone.</p>
                    <p className="mt-3">
                        Support creators across the team, grow your collection,
                        earn more RP, and strengthen your standing within the
                        wider community. The more Runestones you collect, the
                        more your journey through the VRX and VANDERHEIM grows
                        alongside the creators you support.
                    </p>
                </InfoBlock>

                <InfoBlock
                    icon={<Sprout className="h-5 w-5 text-lime-300" />}
                    title="The Journey Continues"
                >
                    <p>Runestones are only the beginning.</p>
                    <p className="mt-3">
                        As the VRX continues to evolve, additional ways to earn
                        Reputation Points (RP) will be introduced through future
                        community features, events, and engagement systems,
                        creating even more opportunities to support your
                        favourite creators while progressing your own standing
                        within the community.
                    </p>
                </InfoBlock>

                <InfoBlock
                    icon={<Link2 className="h-5 w-5 text-zinc-300" />}
                    title="Explore the Rookery"
                >
                    <p>
                        Whether you're looking to forge your Shield, discover
                        new creators, or follow the latest RAVENGUARD activity,
                        everything begins here.
                    </p>
                    <ul className="font-garamond mt-3 grid gap-2 text-zinc-300 sm:grid-cols-2">
                        <li>
                            <a
                                href="https://village.vanderheim.com/"
                                className="text-brand underline-offset-4 hover:underline"
                            >
                                Forge Your Shield
                            </a>
                        </li>
                        <li>
                            <a
                                href="https://ravenguard.vanderheim.com/team"
                                className="text-brand underline-offset-4 hover:underline"
                            >
                                Meet the RAVENGUARD
                            </a>
                        </li>
                        <li>
                            <a
                                href="https://ravenguard.vanderheim.com/live"
                                className="text-brand underline-offset-4 hover:underline"
                            >
                                Who's Live
                            </a>
                        </li>
                        <li>
                            <a
                                href="https://ravenguard.vanderheim.com/schedule"
                                className="text-brand underline-offset-4 hover:underline"
                            >
                                Stream Schedule
                            </a>
                        </li>
                        <li>
                            <a
                                href="https://ravenguard.vanderheim.com/events"
                                className="text-brand underline-offset-4 hover:underline"
                            >
                                Events
                            </a>
                        </li>
                        <li>
                            <a
                                href="https://wallhalla.vanderheim.com/"
                                className="text-brand underline-offset-4 hover:underline"
                            >
                                Shield Wall
                            </a>{" "}
                            (WALLHALLA)
                        </li>
                        <li>
                            <a
                                href="https://www.vanderwood.stream/wallhalla/shield-tags"
                                className="text-brand underline-offset-4 hover:underline"
                            >
                                Shield Tags
                            </a>
                        </li>
                    </ul>
                </InfoBlock>
            </section>

            <section className="border-y border-zinc-800 bg-zinc-900/80 px-4 py-12 sm:px-6">
                <div className="mx-auto w-full max-w-6xl">
                    <SectionTitle
                        icon="🎓"
                        title={
                            <a
                                href="https://ravenguard.vanderheim.com/academy"
                                className="text-brand underline-offset-4 hover:underline"
                            >
                                {convertLetters(
                                    "ABOUT THE VRX ACADEMY (VRX-A)",
                                )}
                            </a>
                        }
                    />
                    <p className="font-garamond mt-3 text-base leading-relaxed text-zinc-300 sm:text-lg">
                        The{" "}
                        <a
                            href="https://ravenguard.vanderheim.com/academy"
                            className="text-brand underline-offset-4 hover:underline"
                        >
                            VRX Academy
                        </a>{" "}
                        is the official pathway into the RAVENGUARD.
                    </p>
                    <p className="font-garamond mt-3 text-base leading-relaxed text-zinc-300 sm:text-lg">
                        Open to creators throughout the VANDERHEIM community,
                        the Academy exists to help streamers grow, collaborate,
                        and become active members of the wider RAVENGUARD
                        ecosystem. Whether you're just beginning your streaming
                        journey or working towards earning your wings, the
                        Academy provides a place to learn, participate, and
                        build your standing within the VRX.
                    </p>
                    <p className="font-garamond mt-3 text-base leading-relaxed text-zinc-300 sm:text-lg">
                        Unlike the RAVENGUARD, membership within the Academy is
                        designed to be broadly accessible, giving creators the
                        opportunity to become part of the wider community while
                        developing their channels and demonstrating their
                        commitment over time.
                    </p>

                    <div className="mt-8 grid gap-10 lg:grid-cols-2">
                        <div>
                            <SectionTitle
                                icon="🛡️"
                                title="How the Academy Works"
                            />
                            <p className="font-garamond mt-3 text-base leading-relaxed text-zinc-300 sm:text-lg">
                                Academy members will have access to standard VRX
                                Academy Runestone, allowing supporters to
                                generate Reputation Points (RP) in exactly the
                                same way as the RAVENGUARD members.
                            </p>
                            <p className="font-garamond mt-3 text-base leading-relaxed text-zinc-300 sm:text-lg">
                                This provides Academy creators with meaningful
                                progression while giving supporters another way
                                to participate in the wider ecosystem.
                            </p>
                            <p className="font-garamond mt-3 text-base leading-relaxed text-zinc-300 sm:text-lg">
                                Like every other part of the VRX, the Academy is
                                fully integrated with the Village Portal,
                                allowing activity, progression, and engagement
                                to contribute towards a creator's long-term
                                standing. Academy members will also be able to
                                update their bio in the Village Portal.
                            </p>
                        </div>
                        <div>
                            <SectionTitle
                                icon={
                                    <Feather className="h-5 w-5 text-zinc-200" />
                                }
                                title="Earning Your Wings"
                            />
                            <p className="font-garamond mt-3 text-base leading-relaxed text-zinc-300 sm:text-lg">
                                Membership within the RAVENGUARD is limited and
                                carefully considered.
                            </p>
                            <p className="font-garamond mt-3 text-base leading-relaxed text-zinc-300 sm:text-lg">
                                As positions become available, Academy members
                                are reviewed using a combination of metrics
                                including community participation, consistency,
                                streaming activity, engagement, and their
                                overall contribution to the wider VANDERHEIM
                                ecosystem.
                            </p>
                            <p className="font-garamond mt-3 text-base leading-relaxed text-zinc-300 sm:text-lg">
                                The Academy is more than a waiting room, it's
                                where future members earn their wings.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function SectionTitle({
    icon,
    title,
}: {
    icon: React.ReactNode;
    title: React.ReactNode;
}) {
    return (
        <h2 className="font-prviking text-brand flex items-center gap-2 text-2xl sm:text-3xl">
            <span className="inline-flex h-6 w-6 items-center justify-center text-base sm:h-7 sm:w-7">
                {icon}
            </span>
            {typeof title === "string" ? convertLetters(title) : title}
        </h2>
    );
}

function InfoBlock({
    icon,
    title,
    children,
}: {
    icon: React.ReactNode;
    title: string;
    children: React.ReactNode;
}) {
    return (
        <article className="border-brand_stream_team mb-6 rounded-xl border bg-zinc-900/70 px-5 py-5 shadow-lg sm:mb-8 sm:px-6 sm:py-6">
            <h3 className="font-prviking text-brand flex items-center gap-2 text-xl sm:text-2xl">
                {icon}
                {convertLetters(title)}
            </h3>
            <div className="font-garamond mt-3 text-base leading-relaxed text-zinc-300 sm:text-lg">
                {children}
            </div>
        </article>
    );
}
